import React, { RefObject } from "react";
import { Row, Spin, message } from "antd";
import StepContainer from "../../components/StepContainer";
import {
  Opportunity,
  Company,
  RadioOptionsInterface,
} from "../../helpers/shared-interfaces";
import { AxiosResponse } from "axios";
import { request, handleValidationErrors } from "../../helpers/utilities";
import BaseForm from "../../components/Form/BaseForm";
import { FormInstance } from "antd/lib/form";
import { ASSIGN_COMPANY_TO_OPPORTUNITY, CREATE_COMPANY, LOOKUP_COMPANY } from "../../gql/companies";

const radioStyle = {
  display: "block",
  height: "30px",
  lineHeight: "30px",
};

export enum Constants {
  CREATE = "create",
}

interface Props {
  setPercentage: (percentage: number) => void;
  getPreviousViewName: () => string | null;
  getNextViewName: (prospect?: string) => string | null;
  opportunity: Opportunity;
  prev: () => void;
  next: () => void;
}

interface State {
  isLoading: boolean;
  companies: Company[];
}

export default class CompanyResults extends React.Component<Props, State> {
  _isMounted = false;
  reference: RefObject<FormInstance>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = { isLoading: true, companies: [] };
    this.reference = React.createRef<FormInstance>();
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  createCompany<T>(name: string): Promise<AxiosResponse<T>> {
    return request({
      query: CREATE_COMPANY,
      variables: { name },
    });
  }

  assignCompanyToOpportunity<T>(companyId: string): Promise<AxiosResponse<T>> {
    const { opportunity } = this.props;
    return request({
      query: ASSIGN_COMPANY_TO_OPPORTUNITY,
      variables: {
        opportunityId: opportunity.id,
        companyId,
      },
    });
  }

  assignCompanyAndContinue = (id: string): void => {
    interface Response {
      data: {
        assignCompanyToOpportunity: {
          opportunity?: Opportunity;
          errors: [];
        };
      };
    }

    this.setState({ isLoading: true });

    this.assignCompanyToOpportunity<Response>(id)
      .then(({ data }: AxiosResponse<Response>): void => {
        if (
          data &&
          data.data &&
          data.data.assignCompanyToOpportunity &&
          data.data.assignCompanyToOpportunity.opportunity &&
          data.data.assignCompanyToOpportunity.errors.length === 0
        ) {
          this.setState({ isLoading: false });

          this.props.next();
        } else {
          message.error("Failed to assign company to opportunity");
        }
      })
      .catch((): void => {
        message.error("Request Failed (Timeout)");
      });
  };

  next = async (): Promise<void> => {
    const fieldsToBeValidated = ["company"];

    interface CompanyCreationResponse {
      data: {
        createCompany: {
          company?: Company;
          errors: [];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      if (data.company === Constants.CREATE) {
        this.setState({ isLoading: true });
        const name: string = this.props.opportunity.companyQuery as string;
        this.createCompany<CompanyCreationResponse>(name).then(
          async ({
            data,
          }: AxiosResponse<CompanyCreationResponse>): Promise<void> => {
            const company = data.data.createCompany.company;
            this.setState({ isLoading: false });
            if (company) {
              await this.assignCompanyAndContinue(company.id);
            } else {
              message.error("Company Already Exists");
            }
          },
        );
      } else {
        await this.assignCompanyAndContinue(data.company);
      }
    } catch (error) {}
  };

  lookupCompany<T>(companyName: string): Promise<AxiosResponse<T>> {
    return request({
      query: LOOKUP_COMPANY,
      variables: {
        companyName,
      },
    });
  }

  createRadioOptions = (): RadioOptionsInterface[] => {
    const data: RadioOptionsInterface[] = this.state.companies.map(
      (company: Company): RadioOptionsInterface => {
        return {
          id: company.id,
          name: company.name,
          value: company.id,
        };
      },
    );

    if (!this.state.isLoading) {
      data.push({
        id: "defaultItem",
        name: `New Company (${this.props.opportunity.companyQuery})`,
        value: Constants.CREATE,
      });
    }

    return data;
  };

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  componentDidMount(): void {
    this._isMounted = true;
    this.props.setPercentage(50);
    this.setState({ isLoading: true });

    interface Response {
      data: {
        lookupCompany: Company[];
      };
    }

    const name: string = this.props.opportunity.companyQuery as string;

    this.lookupCompany<Response>(name).then(
      ({ data }: AxiosResponse<Response>): void => {
        this._isMounted &&
          this.setState({
            isLoading: false,
            companies: data.data.lookupCompany,
          });
      },
      (): void => {
        this._isMounted && this.setState({ isLoading: false });
        message.error("Company lookup failed");
      },
    );
  }

  getPreviousViewName = (): string => {
    return "Company Lookup Page";
  };

  render(): JSX.Element {
    const { isLoading } = this.state;

    return (
      <Spin spinning={isLoading}>
        <StepContainer
          getNextViewName={this.props.getNextViewName}
          getPreviousViewName={this.getPreviousViewName}
          style={{
            position: "relative",
            top: "-10px",
          }}
          config={{
            prev: this.props.prev,
            next: this.next,
            showNext: true,
            showPrev: true,
          }}
        >
          <Row>
            <div
              style={{
                marginBottom: "30px",
              }}
            >
              <BaseForm
                layout="vertical"
                reference={this.reference}
                fields={[
                  {
                    id: "company",
                    name: "company",
                    inputType: "radio",
                    rule: "string",
                    required: true,
                    elementStyle: radioStyle,
                    initialValue: Constants.CREATE,
                    radioOptions: this.createRadioOptions(),
                  },
                ]}
                fieldsToHide={[]}
              />
            </div>
          </Row>
        </StepContainer>
      </Spin>
    );
  }
}
